import { useEffect } from "react";
import { pushNotificationService } from "@/lib/push-notifications";
import type { UserNotification } from "../types";
import { useNotificationMutation } from "./core/use-notification-mutation";

/**
 * Hook to handle push notifications and integrate with the dashboard notification system
 * Works on both web and native platforms through the unified service
 */
export function usePushNotifications() {
  const { markAsRead } = useNotificationMutation();

  useEffect(() => {
    // Setup listeners for push notifications (works on both web and native)

    // Listen for push notifications received in foreground
    const handlePushNotification = (event: CustomEvent<UserNotification>) => {
      const _notification = event.detail;
    };

    // Listen for notification navigation events
    const handleNotificationNavigation = (
      event: CustomEvent<{
        url: string;
        notificationId: string;
        data: any;
      }>
    ) => {
      const { notificationId, url } = event.detail;

      // Mark notification as read when navigation is triggered
      if (notificationId) {
        markAsRead([notificationId]).catch((error) => {
          console.error("Failed to mark push notification as read:", error);
        });
      }

      // Handle navigation
      if (url && url !== "/") {
        // Use React Router navigation if available
        if (window.location.pathname !== url) {
          window.location.href = url;
        }
      }
    };

    // Add event listeners
    window.addEventListener("push-notification-received", handlePushNotification as EventListener);
    window.addEventListener(
      "push-notification-navigation",
      handleNotificationNavigation as EventListener
    );

    // Cleanup
    return () => {
      window.removeEventListener(
        "push-notification-received",
        handlePushNotification as EventListener
      );
      window.removeEventListener(
        "push-notification-navigation",
        handleNotificationNavigation as EventListener
      );
    };
  }, [markAsRead]);

  // Helper functions
  const removeAllNotifications = async () => {
    try {
      await pushNotificationService.removeAllDeliveredNotifications?.();
    } catch (error) {
      console.error("Failed to remove all notifications:", error);
    }
  };

  const getDeliveredNotifications = async () => {
    try {
      return (await pushNotificationService.getDeliveredNotifications?.()) || [];
    } catch (error) {
      console.error("Failed to get delivered notifications:", error);
      return [];
    }
  };

  const getCurrentToken = () => {
    return pushNotificationService.getCurrentToken();
  };

  const isSupported = () => {
    return pushNotificationService.isSupported();
  };

  return {
    removeAllNotifications,
    getDeliveredNotifications,
    getCurrentToken,
    isSupported,
  };
}
